/* oxlint-disable typescript/no-explicit-any -- schema library requires flexible any types */
/* oxlint-disable typescript/no-unsafe-call, typescript/no-unsafe-member-access -- schema library uses any types throughout */
import { BaseSchema } from '../core/base-schema'
import type * as errors from '../core/errors'
import * as types from '../core/types'
import type { DefaultSchema } from './wrappers'
import { _default as makeDefault } from './wrappers'
import type { OptionalSchema, NullableSchema } from '../core/base-schema'

// UFile type (minimal interface for validation)
interface UFileLike {
  size: number
  type: string
  path: string
}

function isUFile(value: unknown): value is UFileLike {
  return (
    typeof value === 'object' &&
    value !== null &&
    'size' in value &&
    'type' in value &&
    'path' in value
  )
}

// UfilePath schema definition
export interface UfilePathSchemaDef extends types.SchemaTypeDef {
  type: 'ufile_path'
}

export interface UfilePathSchemaInternals extends types.SchemaInternals<
  string,
  string | UFileLike
> {
  def: UfilePathSchemaDef
  bag: {
    maxSize?: number
    allowedTypes?: string[]
  }
}

export interface UfilePathSchema extends types.Schema<
  string,
  string | UFileLike,
  UfilePathSchemaInternals
> {
  min(length: 1, message?: string | { message: string | (() => string) }): this
  maxSize(
    bytes: number,
    message?: string | { message: string | (() => string) },
  ): this
  allowedTypes(
    types: string[],
    message?: string | { message: string | (() => string) },
  ): this
  optional(): OptionalSchema<this>
  nullable(): NullableSchema<this>
  default(defaultValue: string | (() => string)): DefaultSchema<this>
  meta(metadata: Record<string, any>): this
  parse(data: unknown, params?: types.ParseContext): string
  safeParse(
    data: unknown,
    params?: types.ParseContext,
  ):
    | { success: true; data: string }
    | { success: false; error: errors.SchemaError }
}

export const UfilePathSchema = types.$constructor<UfilePathSchema>(
  'UfilePathSchema',
  (inst, def: UfilePathSchemaDef) => {
    BaseSchema.init(inst, def)

    // Mark as ufile_path in meta for form generator detection
    inst._zod.meta = { ...inst._zod.meta, isUfilePath: true }

    inst._zod.parse = (payload, _ctx) => {
      const value = payload.value

      // Accept string (already uploaded path)
      if (typeof value === 'string') return payload

      // Accept UFile-like object
      if (isUFile(value)) {
        // For parsing, we pass through - validation checks handle size/type
        return payload
      }

      payload.issues.push({
        code: 'invalid_type',
        expected: 'string or file',
        input: value,
        inst,
      })
      return payload
    }

    // Helper to create check
    const createCheck = (
      checkFn: types.CheckFn<string | UFileLike>,
      onattach: (schema: any) => void = () => {},
    ) => ({
      _zod: {
        def: { check: 'custom' },
        check: checkFn,
        onattach: [onattach],
      },
    })

    // Add a check to the schema
    const addCheck = (check: types.Check<string | UFileLike>) => {
      return new UfilePathSchema({
        ...def,
        checks: [...(def.checks ?? []), check],
      })
    }

    inst.min = (length, message) => {
      if (length !== 1) {
        throw new Error('ufilePath.min() only accepts 1')
      }
      const getMessage = () =>
        typeof message === 'string' ? message : message?.message
      return addCheck(
        createCheck((payload) => {
          const value = payload.value
          // Empty string
          if (typeof value === 'string' && value === '') {
            const msg = getMessage()
            payload.issues.push({
              code: 'too_small',
              type: 'string',
              minimum: 1,
              inclusive: true,
              input: value,
              inst,
              ...(msg ? { message: msg } : { message: 'Required' }),
            })
            return
          }
          // Empty file (size === 0)
          if (isUFile(value) && value.size === 0) {
            const msg = getMessage()
            payload.issues.push({
              code: 'too_small',
              type: 'string',
              minimum: 1,
              inclusive: true,
              input: value,
              inst,
              ...(msg ? { message: msg } : { message: 'Required' }),
            })
          }
        }),
      )
    }

    inst.maxSize = (bytes, message) => {
      const getMessage = () =>
        typeof message === 'string' ? message : message?.message
      return addCheck(
        createCheck(
          (payload) => {
            // Skip validation for strings (already uploaded)
            if (typeof payload.value === 'string') return

            if (isUFile(payload.value) && payload.value.size <= bytes) return

            const msg = getMessage()
            payload.issues.push({
              code: 'custom',
              input: payload.value,
              inst,
              ...(msg ? { message: msg } : { message: 'File too large' }),
            })
          },
          (schema) => {
            schema._zod.bag.maxSize = bytes
          },
        ),
      )
    }

    inst.allowedTypes = (allowedTypes, message) => {
      const getMessage = () =>
        typeof message === 'string' ? message : message?.message
      return addCheck(
        createCheck(
          (payload) => {
            // Skip validation for strings (already uploaded)
            if (typeof payload.value === 'string') return

            if (isUFile(payload.value)) {
              const fileType = payload.value.type
              const isAllowed = allowedTypes.some(
                (allowed) =>
                  fileType === allowed ||
                  fileType.startsWith(allowed.replace('/*', '/')),
              )
              if (isAllowed) return
            }

            const msg = getMessage()
            payload.issues.push({
              code: 'custom',
              input: payload.value,
              inst,
              ...(msg ? { message: msg } : { message: 'Invalid file type' }),
            })
          },
          (schema) => {
            schema._zod.bag.allowedTypes = allowedTypes
          },
        ),
      )
    }

    inst.default = (defaultValue) => makeDefault(inst, defaultValue)
  },
)

export function ufilePath(): UfilePathSchema {
  return new UfilePathSchema({
    type: 'ufile_path',
  })
}
